/**
 * Which surface a deployment calls home — the ONE implementation of the
 * fallback chain, in the dependency-free package because it is read from both
 * sides of a layering boundary.
 *
 * ── The chain ───────────────────────────────────────────────────────────
 *
 *   platform_settings.landing_experience   runtime, operator-set, NULL = not chosen
 *     → brand.yaml experience.landing      build-time default for this deployment
 *       → DEFAULT_BRAND.experience.landing the shipped 'chat'
 *
 * The third link is supplied by the caller as `brandDefault` rather than
 * imported, which is what lets this file stay dependency-free: the brand
 * snapshot lives in `@papan/brand`, whose browser entry point resolves it from
 * a global and whose server entry point resolves it from a file. Both hand the
 * already-resolved value in.
 *
 * ── Why here and not beside the platform-settings resolvers ─────────────
 *
 * Its two sibling resolvers live in
 * `shared/database/src/repositories/platform-settings.ts`, and this one was
 * planned for that file. It cannot go there, for a reason that only shows up
 * one phase later: `@papan/database` pulls in `pg`, so the BROWSER cannot
 * import it — and the browser is a consumer here in a way it is not for a
 * grace ceiling. Three surfaces need this union:
 *
 *   - the repository + the admin route, validating what an operator submits;
 *   - `GET /api/public/experience`, validating on the way OUT so a hand-edited
 *     database cannot put an unknown string on the wire;
 *   - the SPA, validating what it reads back from `localStorage` and from that
 *     endpoint, BEFORE the value is concatenated into a redirect target.
 *
 * That last one is why the duplication would have mattered rather than merely
 * being untidy. The SPA's home redirects (`landingPath` / `landingPathFor`,
 * the `<Navigate>` routes) are safe because they are a fixed prefix plus a
 * literal the build validated. Once the literal arrives over the wire, `isLandingExperience`
 * IS that guarantee — and a second, separately-maintained copy of it in the
 * browser is exactly the shape where one side gets widened and the other does
 * not. Same reasoning as `outranksPlanOverride`, `resolveDisplayCurrency` and
 * `readMonthCap`: a rule consumed across a package boundary belongs below both
 * sides of it, not copied into each.
 *
 * ── The union is closed, and stays closed ───────────────────────────────
 *
 * `BRAND_LANDING_VALUES` in `shared/brand/src/config.ts` remains the AUTHORITY
 * — it is what the brand loader validates `brand.yaml` against at build time.
 * This type, the `platform_settings.landing_experience` column's `$type<>`, and
 * that column's DB `CHECK` all restate it, and
 * `shared/database/src/__tests__/landing-union-sync.test.ts` fails if the four
 * ever disagree. Widening the union is therefore a deliberate edit in four
 * places, which is the intent: an unrecognised value here is both an
 * open-redirect shape and a bounce through the SPA's catch-all forever, with
 * nothing in the UI to explain it.
 */

/** The two surfaces a deployment may open on. */
export type LandingExperience = 'chat' | 'launcher'

/**
 * Is this a landing this build knows how to open?
 *
 * A type guard rather than a comparison written at each call site, because
 * every caller is validating a value from OUTSIDE the type system — a `text`
 * column, a JSON body, a `localStorage` string, an HTTP response — and the
 * places that forget are not visibly different from the places that do not.
 */
export function isLandingExperience(value: unknown): value is LandingExperience {
  return value === 'chat' || value === 'launcher'
}

/**
 * The landing actually in force.
 *
 * `stored` is the operator's override (`null` = "this deployment has not
 * chosen"), `brandDefault` the build-time value from `brand.yaml`.
 *
 * ── Why an explicit membership test and not `stored ?? brandDefault` ────
 *
 * The column is `text`. `??` would forward anything non-null straight through
 * — a value that survived a hand-edited database, a restored dump, or a future
 * migration that widened the CHECK without widening this union. That value
 * does not fail loudly: it reaches a router as a redirect target and bounces
 * through the catch-all, or it is concatenated into a path by `landingPath`.
 * Degrading to the brand default is the only behaviour that stays correct
 * under a stored value nobody anticipated.
 *
 * Note the asymmetry with the two sibling resolvers in
 * `platform-settings.ts`: theirs turn on `stored != null` because for them `0`
 * is a REAL setting that a truthy check would silently discard. Here the
 * hazard runs the other way — an unrecognised non-null must NOT win — so the
 * guard is membership, not nullness. Both are "do not trust the shape of what
 * is stored"; they just have opposite falsy traps.
 */
export function resolveLandingExperience(
  stored: string | null | undefined,
  brandDefault: LandingExperience,
): LandingExperience {
  return isLandingExperience(stored) ? stored : brandDefault
}

/**
 * Where the landing in force came from — for the console, which must say
 * "inherited from this deployment's brand configuration" rather than render a
 * blank field beside a live behaviour.
 *
 * Derived from the same inputs as `resolveLandingExperience` so the label can
 * never disagree with the value it labels.
 */
export type LandingSource = 'setting' | 'brand'

/** Which link of the chain produced the value `resolveLandingExperience` returns. */
export function landingSource(stored: string | null | undefined): LandingSource {
  return isLandingExperience(stored) ? 'setting' : 'brand'
}
